import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/config';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./global-overrides.css"; // Must load after all other CSS to override
import { useSelector, useDispatch } from 'react-redux';
import { clearAuth, fetchUserPermissions } from './redux/features/adminSlice';
import MainLayout from './components/layout/MainLayout';
import { ToastContainer } from 'react-toastify';
import { FeaturePermissionsProvider } from './contexts/FeaturePermissionsContext';
import FeatureProtectedRoute from './components/common/FeatureProtectedRoute';
import AccountTypeProtectedRoute from './components/common/AccountTypeProtectedRoute';
import { NotificationProvider } from './contexts/NotificationContext';
import LanguageProvider from './i18n/components/LanguageProvider';
import LoadingSplash from './components/LoadingSplash/LoadingSplash';
import useTikTokPixel from './hooks/useTikTokPixel';
import useFacebookPixel from './hooks/useFacebookPixel';
import LandingPage from './pages/LandingPage';
import CustomLoginPage from './pages/auth/CustomLoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyCodePage from './pages/auth/VerifyCodePage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// --- Lazy-loaded route components (split into separate webpack chunks) ---
const AdminLayout = React.lazy(() => import("./components/admin/dashboard/layout/AdminLayout"));
const AdminAddSeller = React.lazy(() => import("./components/admin/seller/add/AdminAddSeller"));
const AdminBrandManagement = React.lazy(() => import("./components/admin/brand/AdminBrandManagement"));
const AdminUploadProducts = React.lazy(() => import("./components/admin/products/AdminUploadProducts"));
const AdminHome = React.lazy(() => import('./components/admin/home/AdminHome'));
const AdminRedemptionSettings = React.lazy(() => import("./components/admin/redemption/AdminRedemptionSettings"));
const RedemptionRulesOverview = React.lazy(() => import("./components/admin/redemption/RedemptionRulesOverview"));
const RedemptionRulesHistory = React.lazy(() => import("./components/admin/redemption/RedemptionRulesHistory"));
const SpecialRedeemption = React.lazy(() => import("./components/admin/redemption/special/SpecialRedeemption.js"));
const AdminEditSeller = React.lazy(() => import("./components/admin/seller/edit/AdminEditSeller"));
const AdminEarning = React.lazy(() => import('./components/admin/earning/AdminEarning'));
const AdminTiersSetting = React.lazy(() => import("./components/admin/tiers/AdminTiersSetting"));
const AdminRewardsOptions = React.lazy(() => import('./components/admin/rewards/AdminRewardsOptions'));
const CreateRewards = React.lazy(() => import('./components/admin/rewards/create/CreateRewards'));
const LibraryRewards = React.lazy(() => import('./components/admin/rewards/library/LibraryRewards'));
const RewardView = React.lazy(() => import('./components/admin/rewards/RewardView'));
const GlobalEarning = React.lazy(() => import('./components/admin/earning/global/GlobalEarning'));
const IndividualEarning = React.lazy(() => import('./components/admin/earning/individual/IndividualEarning'));
const AdminTerms = React.lazy(() => import('./components/admin/terms/AdminTerms'));
const TermsAntiFraud = React.lazy(() => import('./components/admin/terms/antiFraud/TermsAntiFraud'));
const TermsInActivity = React.lazy(() => import('./components/admin/terms/inActivity/TermsInActivity'));
const TermsDetectedUsers = React.lazy(() => import('./components/admin/terms/detectedUsers/TermsDetectedUsers'));
const EditSellerProfile = React.lazy(() => import('./components/admin/seller/edit/profile/EditSellerProfile'));
const AdminCampaign = React.lazy(() => import('./components/admin/campaign/AdminCampaign'));
const CampaignsMaster = React.lazy(() => import('./components/admin/campaign/CampaignsMaster'));
const CampaignView = React.lazy(() => import('./components/admin/campaign/CampaignView'));
const CreateCampaign = React.lazy(() => import('./components/admin/campaign/create/CreateCampaign'));
const LibraryCampaign = React.lazy(() => import('./components/admin/campaign/library/LibraryCampaign'));
const KPIDashboard = React.lazy(() => import('./components/admin/dashboard/KPIDashboard'));
const AdminHomeBrands = React.lazy(() => import('./components/admin/home/brands/AdminHomeBrands'));
const NotificationsPage = React.lazy(() => import("./pages/admin/notifications/NotificationsPage"));
const AdminHomeCampaigns = React.lazy(() => import('./components/admin/home/campaigns/AdminHomeCampaigns'));
const ApiKeysManagementPage = React.lazy(() => import('./pages/admin/api-integration/ApiKeysManagementPage'));
const ZohoIntegrateLayout = React.lazy(() => import('./pages/admin/zoho/ZohoIntegrateLayout'));
const ZohoSyncWizard = React.lazy(() => import('./pages/admin/zoho/ZohoSyncWizard'));
const SubscriptionBillingDashboardPage = React.lazy(() => import('./pages/admin/billing/SubscriptionBillingDashboardPage'));
const ChangePlanView = React.lazy(() => import('./pages/admin/subscription/components/ChangePlanView'));
const CampaignPerformance = React.lazy(() => import('./pages/admin/analytics/CampaignPerformance'));
const MemberInsights = React.lazy(() => import('./pages/admin/analytics/MemberInsights'));
const WebhooksDelivery = React.lazy(() => import('./pages/admin/analytics/WebhooksDelivery'));
const SellerPerformance = React.lazy(() => import('./pages/admin/analytics/SellerPerformance'));
const PointsAnalytics = React.lazy(() => import('./pages/admin/analytics/PointsAnalytics'));
const InvoicesPage = React.lazy(() => import('./pages/admin/billing/InvoicesPage'));
const InvoiceDetailPage = React.lazy(() => import('./pages/admin/billing/InvoiceDetailPage'));
const SubscriptionUsagePage = React.lazy(() => import('./pages/admin/billing/SubscriptionUsagePage'));
const BuyOperationsPage = React.lazy(() => import('./pages/admin/billing/BuyOperationsPage'));
const AdminPaymentMethodsPage = React.lazy(() => import('./pages/admin/billing/PaymentMethodsPage'));
// TASK 116 — the customer tax profiles (Egypt): the subscription-management admin's review surface.
const TenantTaxProfilesPage = React.lazy(() => import('./pages/admin/billing/TenantTaxProfilesPage'));
const ApiDocumentation = React.lazy(() => import('./components/admin/api/ApiDocumentation'));
const AuditLogViewer = React.lazy(() => import('./components/admin/sellers/AuditLogViewer/AuditLogViewer'));
const SellerAuditLogViewer = React.lazy(() => import('./components/seller/audit/SellerAuditLogViewer'));
const DocumentationPage = React.lazy(() => import('./pages/admin/documentation/DocumentationPage'));
const WebhookSetup = React.lazy(() => import('./components/admin/api/WebhookSetup'));
// ND-4: TenantLoginPage removed — legacy realm-id path login purged from code
const NewTenantSignupPage = React.lazy(() => import('./pages/NewTenantSignupPage'));
const SignupSuccessPage = React.lazy(() => import('./pages/SignupSuccessPage'));
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const TermsAndConditionsPage = React.lazy(() => import('./pages/TermsAndConditionsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const RefundAndCancellationPolicyPage = React.lazy(() => import('./pages/RefundAndCancellationPolicyPage'));
const ApiDocsPage = React.lazy(() => import('./pages/ApiDocsPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const SolutionsPage = React.lazy(() => import('./pages/SolutionsPage'));
const MultiVendorSolutionPage = React.lazy(() => import('./pages/MultiVendorSolutionPage'));
const UnifiedBrandSolutionPage = React.lazy(() => import('./pages/UnifiedBrandSolutionPage'));
const AntiFraudPage = React.lazy(() => import('./pages/AntiFraudPage'));
const InactivityManagementPage = React.lazy(() => import('./pages/InactivityManagementPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SellerLayout = React.lazy(() => import('./components/seller/layout/SellerLayout'));
const SellerDashboard = React.lazy(() => import('./components/seller/dashboard/SellerDashboard'));
const SellerProducts = React.lazy(() => import('./components/seller/products/SellerProducts'));
const SellerCustomers = React.lazy(() => import('./components/seller/customers/SellerCustomers'));
const SellerCampaigns = React.lazy(() => import('./components/seller/campaigns/SellerCampaigns'));
const SellerEarningRules = React.lazy(() => import('./components/seller/earning/SellerEarningRules'));
const SellerRedemptionRules = React.lazy(() => import('./components/seller/redemption/SellerRedemptionRules'));
const SellerRewards = React.lazy(() => import('./components/seller/rewards/SellerRewards'));
const SellerCashiers = React.lazy(() => import('./components/seller/cashiers/SellerCashiers'));
const SellerCustomerTickets = React.lazy(() => import('./components/seller/customer-support/SellerCustomerTickets'));
const SellerSubscriptionBilling = React.lazy(() => import('./components/seller/billing/SubscriptionBillingDashboardPage'));
const SellerInvoices = React.lazy(() => import('./components/seller/billing/InvoicesPage'));
const SellerCreditsPage = React.lazy(() => import('./pages/seller/SellerCreditsPage'));
const SellerAllowancePage = React.lazy(() => import('./pages/seller/SellerAllowancePage'));
const SellerProfilePage = React.lazy(() => import('./pages/seller/SellerProfilePage'));
const SellerStorePage = React.lazy(() => import('./pages/seller/SellerStorePage'));
const SellerAnalyticsPage = React.lazy(() => import('./pages/seller/SellerAnalyticsPage'));
const SellerApiKeysPage = React.lazy(() => import('./pages/seller/api-integration/SellerApiKeysPage'));
const SellerWebhooksPage = React.lazy(() => import('./pages/seller/api-integration/SellerWebhooksPage'));
const CashierLayout = React.lazy(() => import('./components/cashier/layout/CashierLayout'));
const CashierPOS = React.lazy(() => import('./components/cashier/pos/CashierPOS'));
const CustomerTransactionHistory = React.lazy(() => import('./components/cashier/transactions/CustomerTransactionHistory'));
const CashierRewardsGatewayPage = React.lazy(() => import('./pages/cashier/CashierRewardsGatewayPage'));

// --- API Instance Configuration ---
// REMOVE local apiInstance definition, use global 'api' from redux/api.js


// List of public paths that do not require an authentication check
export const publicPathPatterns = [
  /^\/$/, // Landing page
  /^\/login$/, // New custom login page
  // ND-4: legacy /:realmId/login public path pattern removed (obsolete path-form login)
  /^\/forgot-password$/,
  /^\/verify-code$/,
  /^\/reset-password$/,
  /^\/signup$/,
  /^\/new-tenant-signup$/,
  /^\/signup-success$/,
  /^\/features$/,
  /^\/pricing$/,
  /^\/terms$/,
  /^\/privacy-policy$/,
  /^\/about$/,
  /^\/contact$/,
  /^\/careers$/,
  /^\/refund-cancellation-policy$/,
  /^\/api-docs$/,
  /^\/help$/,
  /^\/integrations$/,
  /^\/solutions$/,
  /^\/solutions\/multi-vendor$/,
  /^\/solutions\/unified-brand$/,
  /^\/anti-fraud$/,
  /^\/inactivity-management$/,
  /^\/blog$/
];

/**
 * Wraps AuditLogViewer to read ?sellerId from URL query params
 * Used by the SellersTable audit icon (4th row-level button)
 */
function AuditLogViewerWithQuery() {
  const [searchParams] = useSearchParams();
  const sellerId = searchParams.get('sellerId') || '';
  return (
    <AuditLogViewer sellerIdFilter={sellerId} />
  );
}

function App() {
  const dispatch = useDispatch();
  // PERMANENT LOOP FIX: start false — Routes tree never blocked on mount.
  // checkSession flips it true→false during the single API round-trip.
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [translationsReady, setTranslationsReady] = useState(false); // Track if critical translations are loaded
  const { isAuthenticated, isLoadingAuth, user } = useSelector((state) => state.loyalty.admin);
  const location = useLocation();
  // Run-once ref guard — prevents a 2nd checkSession() if HMR fast-refreshes
  // the component or React StrictMode double-invokes the effect.
  const sessionChecked = useRef(false);

  // Preload critical translations on mount — only preload namespaces BEYOND
  // the 'common' namespace that i18next-http-backend loads at init.
  // Uses i18n.loadNamespaces() which deduplicates through i18next's internal
  // loading queue (unlike raw fetch which would create duplicate HTTP requests).
  useEffect(() => {
    const preload = async () => {
      try {
        // Preload 'login' via i18next's deduplicated loader so useTranslation('login')
        // in the login page finds the namespace immediately with no "not yet loaded" warning.
        await i18n.loadNamespaces('login');
      } catch {
        // Ignore — fallback keys will display
      }
      setTranslationsReady(true);
    };
    preload();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Session check flag — only isCheckingSession drives the loading gate now.
  // isLoadingAuth (starts false) is kept for legacy thunk compat but no longer
  // used in the loading condition to prevent HMR-restart loops.
  const showLoading = isCheckingSession;

  // Check session status on initial load ONLY — mount-once, ref-guarded.
  // PERMANENT FIX for the cyclic-reload loop:
  //   Old behaviour: isCheckingSession:true on mount → showLoading:true →
  //   !isAuthenticated:true → early return (kills Routes) → React unmounts →
  //   HMR or StrictMode remount → useEffect re-runs → infinite.
  //
  //   New behaviour:
  //   • isCheckingSession starts FALSE → Routes tree always renders.
  //   • sessionChecked ref prevents a 2nd API call on HMR fast-refresh or
  //     React 18 StrictMode double-invocation.
  //   • isCheckingSession is flipped true only for the brief API round-trip,
  //     and the loading overlay renders *inside* the tree (see below), so
  //     React never destroys the router state.
  useEffect(() => {
    // Guard: skip if already ran (HMR fast-refresh or StrictMode double-mount)
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    const path = location.pathname;
    const isPublicPath = publicPathPatterns.some(pattern => pattern.test(path));
    const token = localStorage.getItem('token');

    // Public path + no token → clear any stale auth, nothing to check.
    if (isPublicPath && !token) {
      dispatch(clearAuth());
      return; // isCheckingSession stays false — no loading overlay needed
    }

    const checkSession = async () => {
      setIsCheckingSession(true);
      try {
        console.log('[App.js] Checking session and permissions...');
        await dispatch(fetchUserPermissions()).unwrap();
        console.log('[App.js] Session check complete and permissions loaded');
      } catch (error) {
        console.log('[App.js] Session check failed:', error);
        dispatch(clearAuth());
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get translation function - must be called before any conditional returns
  const { t } = useTranslation('admin');

  // TikTok Pixel — automatic PageView on every route change (must be before any early returns)
  useTikTokPixel();
  // Facebook Pixel — automatic PageView on every route change
  useFacebookPixel();

  // Show loading splash while critical translations are being preloaded
  // This prevents flash of untranslated text (FOUT) on initial load
  if (!translationsReady) {
    return <LoadingSplash />;
  }

  // PERMANENT FIX: do NOT early-return a loading div — that kills the Routes
  // tree and triggers remount → loop. Instead render an overlay inside the
  // tree so React keeps the router alive while the session API resolves.
  // showLoading (= isCheckingSession) is only true for ~200ms on first load.

  // Render routes — loading overlay applied inside the tree, not as early-return
  return (
    <LanguageProvider>
      <div className="App">
      {/* In-tree loading overlay — keeps Routes alive, prevents remount loop */}
      {showLoading && !isAuthenticated && (
        <div style={{ position:'fixed', inset:0, display:'flex', alignItems:'center',
                      justifyContent:'center', background:'#fff', zIndex:9999 }}>
          <LoadingSplash />
        </div>
      )}
      {!isAuthenticated ? ( // Render login routes if NOT authenticated (and not loading)
        <Suspense fallback={<LoadingSplash />}>
        <Routes>
          {/* --- Unauthenticated Routes --- */}
          {/* Route base path to the new Landing Page with MainLayout */}
          <Route path="/" element={
            <MainLayout>
              <LandingPage />
            </MainLayout>
          } />

          {/* NEW: Custom Login Page (without MainLayout for full control) */}
          <Route path="/login" element={<CustomLoginPage />} />

          {/* Reserved portal login paths — all portals (admin/seller/cashier/account) share the
              ONE custom login page and are routed by role after authentication. These MUST be
              declared explicitly: without them, e.g. /admin/login falls through to the generic
              /:realmId/login below, where "admin" is misread as a Keycloak realm -> Keycloak 404. */}
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/seller/login" element={<Navigate to="/login" replace />} />
          <Route path="/cashier/login" element={<Navigate to="/login" replace />} />
          <Route path="/account/login" element={<Navigate to="/login" replace />} />

          {/* ND-4: legacy realm-id path login route removed — all portals use the shared /login */}

          {/* NEW: Custom Forgot Password Flow (without MainLayout) */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-code" element={<VerifyCodePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Redirect old signup path to unified tenant signup flow */}
          <Route path="/signup" element={<Navigate to="/new-tenant-signup" replace />} />
          <Route path="/new-signup" element={<Navigate to="/new-tenant-signup" replace />} />

          {/* New Tenant Signup Page without MainLayout (full-screen experience) */}
          <Route path="/new-tenant-signup" element={<NewTenantSignupPage />} />

          {/* Signup Success Page without MainLayout (full-screen experience) */}
          <Route path="/signup-success" element={<SignupSuccessPage />} />

          {/* New Features Page with MainLayout */}
          <Route path="/features" element={
            <MainLayout>
              <FeaturesPage />
            </MainLayout>
          } />

          {/* New Pricing Page with MainLayout */}
          <Route path="/pricing" element={
            <MainLayout>
              <PricingPage />
            </MainLayout>
          } />

          {/* Terms and Conditions Page with MainLayout */}
          <Route path="/terms" element={
            <MainLayout>
              <TermsAndConditionsPage />
            </MainLayout>
          } />

          {/* Privacy Policy Page with MainLayout */}
          <Route path="/privacy-policy" element={
            <MainLayout>
              <PrivacyPolicyPage />
            </MainLayout>
          } />

          {/* About Us Page with MainLayout */}
          <Route path="/about" element={
            <MainLayout>
              <AboutUsPage />
            </MainLayout>
          } />

          {/* Contact Us Page with MainLayout */}
          <Route path="/contact" element={
            <MainLayout>
              <ContactPage />
            </MainLayout>
          } />

          {/* Careers Page with MainLayout */}
          <Route path="/careers" element={
            <MainLayout>
              <CareersPage />
            </MainLayout>
          } />

          {/* Refund & Cancellation Policy Page with MainLayout */}
          <Route path="/refund-cancellation-policy" element={
            <MainLayout>
              <RefundAndCancellationPolicyPage />
            </MainLayout>
          } />

          {/* API Docs Page with MainLayout */}
          <Route path="/api-docs" element={
            <MainLayout>
              <ApiDocsPage />
            </MainLayout>
          } />

          {/* Help Center Page with MainLayout */}
          <Route path="/help" element={
            <MainLayout>
              <HelpPage />
            </MainLayout>
          } />

          {/* Integrations Page with MainLayout */}
          <Route path="/integrations" element={
            <MainLayout>
              <IntegrationsPage />
            </MainLayout>
          } />

          {/* Solutions Pages with MainLayout */}
          <Route path="/solutions" element={
            <MainLayout>
              <SolutionsPage />
            </MainLayout>
          } />
          <Route path="/solutions/multi-vendor" element={
            <MainLayout>
              <MultiVendorSolutionPage />
            </MainLayout>
          } />
          <Route path="/solutions/unified-brand" element={
            <MainLayout>
              <UnifiedBrandSolutionPage />
            </MainLayout>
          } />

          {/* Anti-Fraud & Inactivity Management Pages */}
          <Route path="/anti-fraud" element={
            <MainLayout>
              <AntiFraudPage />
            </MainLayout>
          } />
          <Route path="/inactivity-management" element={
            <MainLayout>
              <InactivityManagementPage />
            </MainLayout>
          } />

          {/* Custom 404 Page for unauthenticated state */}
          <Route path="*" element={
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          } />

        </Routes>
        </Suspense>
      ) : ( // Render protected routes if Authenticated (and not loading)
        <NotificationProvider> {/* Wrap with NotificationProvider */}
          <FeaturePermissionsProvider>
            <Suspense fallback={<LoadingSplash />}>
            <Routes>
              {/* --- Authenticated Routes --- */}

              {/* Redirect base path to the default authenticated route based on user role */}
              <Route path="/" element={
                user?.role_id === 3
                  ? <Navigate to="/seller/dashboard" replace />
                  : user?.role_id === 4
                  ? <Navigate to="/cashier/pos" replace />
                  : <Navigate to="/admin/dashboard" replace />
              } />

            {/* Redirect away from login pages if already authenticated */}
            <Route path="/login" element={
              user?.role_id === 3
                ? <Navigate to="/seller/dashboard" replace />
                : user?.role_id === 4
                ? <Navigate to="/cashier/pos" replace />
                : <Navigate to="/admin/dashboard" replace />
            } />
            <Route path="/admin/login" element={
              user?.role_id === 3
                ? <Navigate to="/seller/dashboard" replace />
                : user?.role_id === 4
                ? <Navigate to="/cashier/pos" replace />
                : <Navigate to="/admin/dashboard" replace />
            } />
            {/* ND-4: legacy realm-id path login route removed */}

            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              // AdminProtectedRoute is likely redundant now if App.js handles the main auth check
              // You might simplify this later
              element={<AdminLayout />} // Render AdminLayout directly
            >
              {/* Default route within /admin */}
              <Route index element={<Navigate to="dashboard" replace />} />

              {/* Dashboard route - standalone KPI Dashboard */}
              <Route path="dashboard" element={<KPIDashboard />} />

              {/* Nested routes within AdminLayout */}
              <Route path="home" element={<AdminHome />}>
                {/* Default route within /admin/home - do NOT land on the per-seller
                    Brands page (data-scope plan 2026-09-15); redirect to dashboard */}
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                {/* Brands home page hidden for multivendor (per-seller brand rows) */}
                <Route path="brands" element={<AccountTypeProtectedRoute redirectTo="/admin/dashboard" />}>
                  <Route index element={<AdminHomeBrands />} />
                </Route>
                {/* Campaigns home hidden for multivendor (data-scope 2026-09-15) */}
                <Route path="campaigns" element={<AccountTypeProtectedRoute redirectTo="/admin/dashboard" />}>
                  <Route index element={<AdminHomeCampaigns />} />
                </Route>
              </Route>
              {/* --- Other nested admin routes --- */}
              {/* ... tiers-setting ... */}
              {/* ... rewards-options ... */}
              {/* ... earning-rules ... */}
              {/* ... redemption-rules ... */}
              {/* ... terms&conditions ... */}
              {/* ... manage-campaign ... */}
              {/* ... campaign-templates ... */}
              {/* ... brand-management ... */}
              {/* ... products-upload ... */}
              {/* ... add-seller ... */}
              {/* ... edit-seller ... */}
               {/* Tier Settings hidden for multivendor (part of Campaigns group, data-scope 2026-09-15) */}
               <Route element={<AccountTypeProtectedRoute />}>
                 <Route path='tiers-setting' element={<AdminTiersSetting />} />
               </Route>
              {/* Multi-Vendor account portal: account-level loyalty rule pages are hidden
                  (each shop runs its own loyalty program). Guarded by account_type. */}
              <Route element={<AccountTypeProtectedRoute />}>
              <Route path="rewards-options" element={<AdminRewardsOptions />}>
                <Route index element={<Navigate to="create" replace />} />
                <Route path='create' element={<CreateRewards />} />
                <Route path='library' element={<LibraryRewards />} />
                <Route path='view/:rewardId' element={<RewardView />} />
              </Route>
              <Route path="earning-rules" element={<AdminEarning />} >
                <Route index element={<Navigate to="global" replace />} />
                <Route path='global' element={<GlobalEarning />} />
                <Route path='individual' element={<IndividualEarning />} />
              </Route>
              <Route path="redemption-rules/*" element={<AdminRedemptionSettings />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<RedemptionRulesOverview />} />
                <Route path="configure" element={<SpecialRedeemption />} />
                <Route path="history" element={<RedemptionRulesHistory />} />
              </Route>
              <Route path="terms&conditions" element={<AdminTerms />}>
                <Route index element={<Navigate to="antiFraud" replace />} />
                <Route path="antiFraud" element={<TermsAntiFraud />} />
                <Route path="inActivity" element={<TermsInActivity />} />
                <Route path="detectedUsers" element={<TermsDetectedUsers />} />
              </Route>
              </Route>
              {/* Manage Campaign hidden for multivendor (no settlement mechanism, data-scope 2026-09-15) */}
              <Route element={<AccountTypeProtectedRoute />}>
                <Route path='manage-campaign' element={<AdminCampaign />}>
                  <Route element={<FeatureProtectedRoute featureFlag="campaign-engine-access" featureName="Campaign Engine" description="Campaign Engine is not included in your current plan. Upgrade to access advanced campaigns, rules, and templates." />}>
                    <Route index element={<CampaignsMaster />} />
                    <Route path='view/:campaignId' element={<CampaignView />} />
                    <Route path='create' element={<CreateCampaign />} />
                    <Route path='library' element={<LibraryCampaign />} />
                  </Route>
                </Route>
              </Route>
               {/* Manage Rules page removed - functionality consolidated into Manage Campaign */}

              {/* <Route path="global-earning" element={<AdminEarningSettings />} /> */}
              <Route element={<AccountTypeProtectedRoute />}>
                <Route path="brand-management" element={<AdminBrandManagement />} />
              </Route>
              <Route path="products-upload" element={<AdminUploadProducts />} />
              <Route path="add-seller" element={<AdminAddSeller />} />
              <Route path="edit-seller" element={<AdminEditSeller />} />
              <Route path='edit-seller/profile' element={<EditSellerProfile />} />

               {/* API Integration Routes */}
               {/* RAG AI System active - Documentation page temporarily hidden (use /ask endpoint for faster knowledge access) */}
               {/* <Route path="documentation" element={<DocumentationPage />} /> */}
               <Route path="api-keys" element={<ApiKeysManagementPage />} />
              <Route path="setup" element={<WebhookSetup />} />
              <Route path="zoho-integrate" element={<ZohoIntegrateLayout />}>
                <Route index element={<ZohoSyncWizard />} />
                <Route path="branches" element={<Navigate to="/admin/zoho-integrate" replace />} />
                <Route path="products" element={<Navigate to="/admin/zoho-integrate" replace />} />
                <Route path="customers" element={<Navigate to="/admin/zoho-integrate" replace />} />
                <Route path="orders" element={<Navigate to="/admin/zoho-integrate" replace />} />
              </Route>

              {/* Subscription & Billing Routes */}
              {/* More specific routes must come before parent route */}
              <Route path="subscription-billing/change-plan" element={<ChangePlanView />} />
              <Route path="subscription-billing/invoices/:invoiceId" element={<InvoiceDetailPage />} />
              <Route path="subscription-billing/invoices" element={<InvoicesPage />} />
              <Route path="subscription-billing/usage" element={<SubscriptionUsagePage />} />
              <Route path="subscription-billing/buy-operations" element={<BuyOperationsPage />} />
              <Route path="subscription-billing/payment-methods" element={<AdminPaymentMethodsPage />} />
              {/* TASK 116 — the Egyptian customer tax profiles: the list, the card, the review,
                  the delete and the Excel workbook. Gated inside the page on the ACCOUNT user
                  (role 1/2): a seller never sees it. */}
              <Route path="subscription-billing/tax-profiles" element={<TenantTaxProfilesPage />} />
              {/* Parent route must be last */}
              <Route path="subscription-billing" element={<SubscriptionBillingDashboardPage />} />

              {/* Notifications Routes */}
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<Navigate to="/seller/dashboard" replace />} />

              {/* Analytics Routes */}
              {/* Campaign Performance hidden for multivendor (campaign-related, data-scope 2026-09-15) */}
              <Route element={<AccountTypeProtectedRoute />}>
                <Route path="campaign-performance" element={<FeatureProtectedRoute featureFlag="campaign-performance-access" featureName="Campaign Performance" description="Campaign analytics are not included in your current plan. Upgrade to access campaign performance reports." />}>
                  <Route index element={<CampaignPerformance />} />
                </Route>
              </Route>
              <Route path="member-insights" element={<MemberInsights />} />
              <Route path="seller-performance" element={<SellerPerformance />} />
              <Route path="points-analytics" element={<PointsAnalytics />} />
              {/* Webhooks & Delivery — delivery-health monitor. Sidebar gating is
                  'webhook-setup-access' (AdminLayout menu group id 3). */}
              <Route path="webhook-analytics" element={<WebhooksDelivery />} />

              {/* Audit Log */}
              <Route path="audit-log" element={<AuditLogViewerWithQuery />} />

              {/* Add a catch-all within the authenticated /admin route if needed */}
              {/* <Route path="*" element={<div>Admin 404</div>} /> */}

            </Route>

            <Route path="/seller" element={<SellerLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<SellerDashboard />} />
              <Route path="products" element={<SellerProducts />} />
              <Route path="customers" element={<SellerCustomers />} />
              <Route path="customers/:customerId" element={<SellerCustomers />} />
              <Route path="campaigns" element={<SellerCampaigns />} />
              <Route path="earning-rules" element={<SellerEarningRules />} />
              <Route path="redemption-rules" element={<SellerRedemptionRules />} />
              <Route path="rewards" element={<SellerRewards />} />
              <Route path="cashiers" element={<SellerCashiers />} />
              <Route path="customer-support" element={<SellerCustomerTickets />} />
              <Route path="subscription-billing" element={<SellerSubscriptionBilling />} />
              <Route path="subscription-billing/invoices" element={<SellerInvoices />} />
              <Route path="subscription-billing/payment-methods" element={<Navigate to="/seller/subscription-billing" replace />} />
              <Route path="credits-billing" element={<SellerCreditsPage />} />
              <Route path="my-allowance" element={<SellerAllowancePage />} />
              <Route path="profile" element={<SellerProfilePage />} />
              <Route path="store" element={<SellerStorePage />} />
              {/* TASK 16: /seller/analytics is NO LONGER page-wide gated. Every marketplace
                  seller must be able to OPEN the page; the entitlement
                  ('seller-scoped-analytics-access') is enforced INSIDE the page by its own
                  modules (SellerAnalyticsPage -> FeatureGate -> UpgradePrompt), so an
                  unentitled seller sees the locked module, not a replaced page. */}
              <Route path="analytics" element={<SellerAnalyticsPage />} />
              {/* Seller API Keys & Webhooks (MULTI_VENDOR Integrations, 2026-09-01) */}
              <Route path="api-keys" element={<SellerApiKeysPage />} />
              <Route path="webhooks" element={<SellerWebhooksPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="audit-log" element={<SellerAuditLogViewer />} />
              <Route path="settings" element={<Navigate to="/seller/dashboard" replace />} />
              {/* Seller-scoped Terms & Conditions (plan task 5). Reuses the admin terms
                  components with scope="seller" so both portals share one implementation. */}
              <Route path="terms" element={<AdminTerms />}>
                <Route index element={<Navigate to="antiFraud" replace />} />
                <Route path="antiFraud" element={<TermsAntiFraud scope="seller" />} />
                <Route path="inActivity" element={<TermsInActivity scope="seller" />} />
                <Route path="detectedUsers" element={<TermsDetectedUsers scope="seller" />} />
              </Route>
            </Route>

            {/* Cashier Portal Routes */}
            <Route path="/cashier" element={<CashierLayout />}>
              <Route index element={<Navigate to="pos" replace />} />
              <Route path="pos" element={<CashierPOS />} />
              <Route path="rewards" element={<Navigate to="/cashier/rewards-gateway" replace />} />
              <Route path="rewards-gateway" element={<CashierRewardsGatewayPage />} />
              <Route path="rewards-gateway/:customerId" element={<CashierRewardsGatewayPage />} />
              <Route path="transactions" element={<CustomerTransactionHistory />} />
            </Route>

            {/* Catch-all within authenticated state — same branded 404 as the
                unauthenticated branch. Replaces the plain-text div that leaked the
                raw i18n key 'ui.not404Found' and had no layout/branding. */}
            <Route path="*" element={
              <MainLayout>
                <NotFoundPage />
              </MainLayout>
            } />

            </Routes>
            </Suspense>
          </FeaturePermissionsProvider>
        </NotificationProvider>
      )}
        <ToastContainer
          pauseOnFocusLoss={false}
          pauseOnHover={false}
        />
      </div>
    </LanguageProvider>
  );
}

export default App;// hot-reload-verification-touch-2026-09-02
// hmr-verify-02-2026// hmr-verify-03
// hmr-verify-04
